// OWNERSHIP — the parts the Ägarbyte stage is built from, ported from the reference
// (components/ownership/OwnershipParts.tsx).
//
// Ägarbyte is ONE stage seen from two sides, so both sides are composed from the same pieces at
// the same measure as the payment stage (the post-signing 42rem family): the deal's identity
// band, a kicker with the quiet status dot, a sentence H1 and open ruled rows. No cards, no
// checklist, no success mark.
//
// The identity band, the heading scale and the lead/meta measures are the payment stage's own —
// imported rather than restated, so the two post-signing stages cannot drift apart. The
// reference keeps a second copy of those constants; here there is one.
import React, { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { StatusDot } from "../StatusMark";
import { H1_CLASS, LEAD_CLASS, META_CLASS, PRIMARY_ACTION } from "../payment/PaymentParts";

export { H1_CLASS, LEAD_CLASS, META_CLASS };

/** The post-signing stage's own measure (max-w-2xl); never the rail's. */
export const OWNERSHIP_CONTENT_MAX = "42rem";

/** Slate while the seller has not reported the transfer; green only once they have. */
export function OwnershipKicker({ reported, t }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <StatusDot tone={reported ? "green" : "slate"} />
      <span className="text-[13px] font-semibold tracking-[0.02em] text-ds-muted">{t("ownership.kicker")}</span>
    </div>
  );
}

/**
 * `reported`, for either party: what was said, by whom, and what happens next at
 * Transportstyrelsen. It never says the owner is registered.
 *
 * `announce` decides how the resolution reaches assistive technology — once, never twice:
 *   "focus"  the party who pressed the button has lost it, so focus lands on the heading, which
 *            is then what is read (the seller, live);
 *   "none"   the stage announces it itself — the buyer's page from a live region that was already
 *            there, and a stage found already reported through its own arrival.
 *
 * `onContinue` is for a return to an already reported deal only, never the live path: a statement
 * that arrived while the party waited moves them on by itself.
 */
export function OwnershipResolved({ lead, meta, announce, onContinue, continuing, t }) {
  const headingRef = useRef(null);

  useEffect(() => {
    if (announce === "focus") headingRef.current?.focus({ preventScroll: true });
  }, [announce]);

  return (
    <div data-testid="ownership-reported">
      <OwnershipKicker reported t={t} />
      <h1 ref={headingRef} tabIndex={-1} className={`ds-arrival-heading ${H1_CLASS} focus:outline-none`}>
        {t("ownership.reported.title")}
      </h1>
      <p className={LEAD_CLASS}>{lead}</p>
      <p className={`${META_CLASS} mt-2`}>{meta}</p>
      {onContinue && (
        <div className="mt-7">
          <button type="button" onClick={onContinue} disabled={continuing} className={PRIMARY_ACTION}
            data-testid="ownership-continue">
            {t("ownership.continue")}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Open ruled rows in the payment rows' family. A copied value is acknowledged in the row itself —
 * the mark turns into a tick and, where there is room, the label says so — with no toast, and
 * nothing moves.
 *
 * Each row: { key, label, value, copyValue? }. `copyValue` is always the WHOLE value, so a masked
 * row still copies the real thing — the seller is typing it into Transportstyrelsen, not reading
 * it off the screen.
 */
export function OwnershipLedger({ rows, header, t }) {
  const [copiedKey, setCopiedKey] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const copy = (row) => {
    if (!row.copyValue) return;
    try {
      navigator.clipboard?.writeText(row.copyValue);
    } catch (_) {
      /* no clipboard (an insecure origin, a locked-down browser): the value is still on screen */
    }
    setCopiedKey(row.key);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="mt-6" data-testid="ownership-ledger">
      {header}
      <dl className="m-0 border-t border-[rgba(20,36,46,.25)]">
        {rows.map((row) => {
          const copied = copiedKey === row.key;
          return (
            <div key={row.key}
              className="flex min-h-[48px] items-center justify-between gap-3 border-b border-ds-rule-row sm:min-h-[52px]"
              data-testid={`ownership-row-${row.key}`}>
              <dt className="ds-label py-2">
                {row.label}
                {copied && (
                  <span className="ml-2 hidden font-medium text-ds-green-deep sm:inline" aria-hidden="true">
                    {t("ownership.copied")}
                  </span>
                )}
              </dt>
              <dd className="m-0 flex items-center gap-1 text-right text-[15px] font-semibold text-ds-ink">
                <span className="tabular-nums">{row.value}</span>
                {row.copyValue ? (
                  <button type="button" onClick={() => copy(row)}
                    aria-label={t("ownership.copy", { label: String(row.label).toLowerCase() })}
                    className="-mr-3 inline-flex h-11 w-11 flex-none items-center justify-center rounded-[9px] text-ds-muted transition-colors hover:text-ds-ink">
                    {copied
                      ? <Check className="h-4 w-4 text-ds-green" aria-hidden="true" />
                      : <Copy className="h-4 w-4" aria-hidden="true" />}
                  </button>
                ) : (
                  <span className="-mr-3 w-11 flex-none" aria-hidden="true" />
                )}
              </dd>
            </div>
          );
        })}
      </dl>
      <span className="sr-only" role="status" aria-live="polite">{copiedKey ? t("ownership.copied") : ""}</span>
    </div>
  );
}
